import z from "zod";
import {
  nomeCognomeRegex,
  permissions,
  phoneRegex,
  required_error,
  endpoint,
  passwordSchema,
} from "../../services/shared";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Snackbar from "@mui/material/Snackbar";
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { FormLabel } from "../../components/form-label/FormLabel";
import React, { useEffect, useState } from "react";
import axios, {AxiosError} from "axios";
import EnhancedUserTable from "../../components/table-user/UserDenseTable";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
} from "@firebase/firestore";
import { db } from "../../services/firebase";
import {useAuth} from "../../providers/AuthProvider";
import {apiClient} from "../../services/api";
const defaultValues = {
  displayName: undefined,
  email: undefined,
  password: undefined,
  active: "",
  phoneNumber: undefined,
  phonePrefix: "" as any,
  permissions: [],
  org: {},
};
const PermissionItemSchema = z.object({
  label: z.string(),
  grant: z.string(),
});
const OrgItemSchema = z.object({
  displayName: z
    .string({ required_error })
    .min(2, "Il nome visualizzato deve contenere almeno 2 caratteri"),
  uid: z.string({ required_error }).min(1, "UID non può essere vuoto"),
});
type FormData = {
  displayName: string;
  email: string;
  password?: string;
  active: string;
  phoneNumber: string;
  phonePrefix: "+39" | "+355";
  permissions: { label: string; grant: string }[];
  org: { displayName: string; uid: string };
};
export default function CreateUser() {
  const {freshToken} = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [activeUser, setActiveUser] = useState<any>(undefined);
  const [method, setMethod] = useState<any>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });

  const getSchema = (editMode: boolean) => {
    return z.object({
      displayName: z
        .string({ required_error, invalid_type_error: "Formato non valido" })
        .min(2, "Il nome è obbligatorio")
        .regex(nomeCognomeRegex, "Il nome può contenere solo lettere e spazi"),
      email: z.string({ required_error }).email("Email non valida"),
      password: !editMode ? passwordSchema : z.string().optional(),
      active: z.string({required_error}),
      phoneNumber: z.string({
        required_error,
        invalid_type_error: "Formato non valido",
      }),
      phonePrefix: z.enum(["+39", "+355"]),
      permissions: z.array(PermissionItemSchema).min(1),
      org: OrgItemSchema,
    });
  };

  const form = useForm<FormData>({
    resolver: (data, context, options) =>
      zodResolver(getSchema(!!activeUser))(data, context, options),
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;
  const onSubmit = handleSubmit(async (data): Promise<void> => {
    setSnackState(false);
    let res = null;
    const phone = data.phoneNumber.toString().toLocaleLowerCase().trim();
    const payload =
      method === "create"
        ? {
            displayName: data.displayName.toString().toLocaleLowerCase().trim(),
            phoneNumber: `${data.phonePrefix}${phone}`,
            password: data.password ? data.password.toString() : "123Pass!",
            email: data.email.toString().toLocaleLowerCase().trim(),
            active: data.active === "attivo",
            grants: data.permissions.map((item) => item.grant),
            role: "operatore",
            org: data.org.uid,
          }
        : {
            uid: activeUser.uid,
            displayName: data.displayName.toString().toLocaleLowerCase().trim(),
            phoneNumber: `${data.phonePrefix}${phone}`,
            email: data.email.toString().toLocaleLowerCase().trim(),
            active: data.active === "attivo",
            grants: data.permissions.map((item) => item.grant),
            role: "operatore",
            org: data.org.uid,
          };
    const freshTK = await freshToken();
    try {
      res = await apiClient.post(method === "create" ?
          `/v1/users/${method}` :
          `/v1/users/${method}/${payload.uid}`
          , payload, {
        headers: {
          "Content-Type": `application/json`,
        },
      });
    } catch (e: AxiosError | any) {
      console.error("errore", e);
      setSnackMessage({
        severity: "error",
        message: e.response.data.error,
      });
      setSnackState(true);
    }
    if (res?.status && res?.status >= 200 && res?.status < 300) {
      setSnackMessage({ severity: "success", message: method === "create" ? "Utente creato" : "Utente aggiornato" });
      setSnackState(true);
      setActiveUser(undefined);
    }
  });
  useEffect(() => {
    if (activeUser) {
      setMethod("update");
      const userToEdit = {
        ...activeUser,
        phoneNumber: activeUser.phoneNumber?.replace(/^\+39|\+355/, ""), // rimuove il prefisso
        phonePrefix: activeUser.phoneNumber?.startsWith("+355")
          ? "+355"
          : "+39",
        active: activeUser.active ? "attivo" : "sospeso",
        permissions:
          permissions.filter((item) =>
            activeUser?.grants?.includes(item.grant),
          ) ?? [],
        org: orgs.find((item) => item.uid === activeUser.org),
      };
      reset(userToEdit);
    } else {
      setMethod("create");
      reset(defaultValues);
    }
  }, [activeUser]);
  // Recupero utenti
  useEffect(() => {
    const colRef = collection(db, "users");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const data = querySnapshot.docs.map((d) => ({
          uid: d.id,
          displayName: d.data().displayName,
          phoneNumber: d.data().phoneNumber,
          active: d.data()?.active,
          grants: d.data().grants,
          role: d.data().role,
          email: d.data().email,
          org: d.data().org,
        }));
        setUsers(data);
        setLoading(false);
      },
      (error) => {
        console.error("Errore nel listener realtime:", error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);
  // Recupero organizzazioni
  useEffect(() => {
    const colRef = collection(db, "organizations");
    const q = query(
      colRef,
      where("active", "==", true),
      orderBy("created_at", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const data = querySnapshot.docs.map((d) => ({
          uid: d.id,
          displayName: d.data().ragioneSociale,
        }));
        setOrgs(data);
        setLoading(false);
      },
      (error) => {
        console.error("Errore nel listener realtime:", error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);

  return (
    <div className={"container"}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
      <FormLabel label={"Informazioni utente"}></FormLabel>
      <form
        onSubmit={onSubmit}
        style={{
          padding: "1em",
          borderRadius: "1em",
          margin: "auto",
          marginBottom: "1em",
          maxWidth: "100%",
          background: "white",
        }}
        className="flex-col gap-2"
      >
        <div className="flex-row gap-2">
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Username"
                placeholder="Es. Massimo Rossi"
                value={field.value ?? ""}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
        </div>
        <div className="flex-row gap-2">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Email"
                value={field.value || ""}
                placeholder="Inserisci email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
          {!activeUser && (
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Password"
                  placeholder="Es. Super1999@"
                  value={field.value ?? ""}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              )}
            />
          )}
        </div>
        <div className="flex-row gap-2">
          <Controller
            name="phonePrefix"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%", maxWidth: "125px" }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="tipologia-label">Prefisso</InputLabel>
                  <Select
                    {...field}
                    style={{ textAlign: "left" }}
                    labelId="phone-prefix"
                    value={field.value || ""}
                    error={!!errors.phonePrefix}
                  >
                    <MenuItem value="" disabled>
                      Seleziona una opzione
                    </MenuItem>
                    <MenuItem value="+39">🇮🇹{" +39"}</MenuItem>
                    <MenuItem value="+355">🇦🇱{" +355"}</MenuItem>
                  </Select>
                  {errors.phonePrefix && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      {errors.phonePrefix.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Cellulare"
                value={field.value || ""}
                placeholder="Inserisci cellulare"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
              />
            )}
          />
          <Controller
            name="permissions"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                fullWidth={true}
                options={permissions}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : (option?.label ?? "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.grant === value.grant
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={"small"}
                    fullWidth={true}
                    label="Cerca autorizzazione"
                    error={!!errors.permissions}
                    helperText={errors.permissions?.message}
                  />
                )}
              />
            )}
          />
        </div>
        <div className={"flex-row gap-2"}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%", maxWidth: "225px" }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="active">Stato utente</InputLabel>
                  <Select
                    {...field}
                    style={{ textAlign: "left" }}
                    labelId="active"
                    value={field.value || ""}
                    error={!!errors.active}
                  >
                    <MenuItem value="" disabled>
                      Seleziona una opzione
                    </MenuItem>
                    <MenuItem value={"attivo"}>Attivo</MenuItem>
                    <MenuItem value={"sospeso"}>Sospeso</MenuItem>
                  </Select>
                  {errors.active && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      {errors.active.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
          <Controller
            name="org"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth={true}
                options={orgs}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : (option?.displayName ?? "")
                }
                isOptionEqualToValue={(option, value) =>
                  option.uid === value.uid
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={"small"}
                    fullWidth={true}
                    label="Cerca organizzazione"
                    error={!!errors.org}
                    helperText={errors.org?.uid?.message}
                  />
                )}
              />
            )}
          />
        </div>
        {!activeUser ? (
          <Button variant={"contained"} type={"submit"} disabled={isSubmitting}>
            {isSubmitting ? "Elaboro richiesta" : "Crea"}
          </Button>
        ) : (
          <div className="flex-row gap-2">
            <Button
              variant={"contained"}
              type={"submit"}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Elaboro richiesta" : "Modifica"}
            </Button>
            <Button
              variant={"outlined"}
              type={"button"}
              disabled={isSubmitting}
              onClick={() => setActiveUser(undefined)}
            >
              {isSubmitting ? "Elaboro richiesta" : "Annulla modifiche"}
            </Button>
          </div>
        )}
      </form>
      <FormLabel label={"Lista utenti"}></FormLabel>
      <EnhancedUserTable
        data={users}
        onSelect={setActiveUser}
      ></EnhancedUserTable>
    </div>
  );
}
